import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, TrendingUp, ArrowUpDown, X } from 'lucide-react';
import { serviceCallsApi, TeamPerformance, TeamPerfStaff } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import { dur, todayIST } from './ServiceTeam';

/**
 * The whole service team over a date range, on one screen: a ranking of
 * every person, a day-by-day grid, and — when one person is picked — their
 * own days set against the team average.
 *
 * Every figure is the daily Service Team board added up, so it agrees with
 * the board and the day timeline for any single day. Pending and on-hold are
 * as of now.
 */

const addDays = (d: string, n: number) => {
  const t = new Date(d + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10);
};
const PRESETS: Array<{ key: string; label: string; range: () => [string, string] }> = [
  { key: 'today', label: 'Today', range: () => [todayIST(), todayIST()] },
  { key: 'week', label: 'This week', range: () => {
    const t = todayIST(); const dow = (new Date(t + 'T00:00:00Z').getUTCDay() + 6) % 7; return [addDays(t, -dow), t];
  } },
  { key: 'month', label: 'This month', range: () => [todayIST().slice(0, 8) + '01', todayIST()] },
  { key: 'last', label: 'Last month', range: () => {
    const first = todayIST().slice(0, 8) + '01'; const lastEnd = addDays(first, -1); return [lastEnd.slice(0, 8) + '01', lastEnd];
  } },
  { key: '30', label: 'Last 30 days', range: () => [addDays(todayIST(), -29), todayIST()] },
];

// Working time added up over days: in hours, never "days" (which would read as calendar days).
const hm = (m: number | null | undefined) => {
  if (!m || m <= 0) return '—';
  const h = Math.floor(m / 60), r = m % 60;
  return h ? `${h}h ${r}m` : `${r}m`;
};
const pctText = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v}%`);
const hours = (m: number) => (m > 0 ? `${Math.round(m / 6) / 10}h` : '—');
const dayLabel = (d: string) => {
  const t = new Date(d + 'T00:00:00Z');
  return { dm: `${d.slice(8, 10)}/${d.slice(5, 7)}`, wd: t.toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'UTC' }) };
};

type SortKey = 'name' | 'days_present' | 'completed' | 'completed_per_day' | 'avg_completion_minutes' | 'max_completion_minutes'
  | 'busy_pct' | 'free_minutes' | 'idle_while_waiting_minutes' | 'idle_pct' | 'days_idle_30' | 'holds' | 'pending_over_24h';

const COLS: Array<{ key: SortKey; label: string; title: string; right?: boolean }> = [
  { key: 'name', label: 'User', title: 'Service staff' },
  { key: 'days_present', label: 'Days', title: 'Days checked in', right: true },
  { key: 'completed', label: 'Completed', title: 'Calls closed in the range', right: true },
  { key: 'completed_per_day', label: 'Per day', title: 'Completed per day present', right: true },
  { key: 'avg_completion_minutes', label: 'Avg time', title: 'Taken to closed, held time left out', right: true },
  { key: 'max_completion_minutes', label: 'Max time', title: 'The longest single call', right: true },
  { key: 'busy_pct', label: 'Busy %', title: 'Share of shift time spent on a call', right: true },
  { key: 'free_minutes', label: 'Free', title: 'Shift time with no call in progress', right: true },
  { key: 'idle_while_waiting_minutes', label: 'Idle while calls waited', title: 'Free while a call sat unallocated', right: true },
  { key: 'idle_pct', label: 'Idle %', title: 'Idle while calls waited, as a share of shift time', right: true },
  { key: 'days_idle_30', label: 'Days idle 30m+', title: 'Days with 30 minutes or more idle while calls waited', right: true },
  { key: 'holds', label: 'Holds', title: 'Times a call was put on hold', right: true },
  { key: 'pending_over_24h', label: 'Pending >24h', title: 'In hand for 24 hours or more, right now', right: true },
];

// Higher is better for these; lower is better for the "low" ones.
const GOOD_HIGH: SortKey[] = ['completed', 'completed_per_day', 'busy_pct', 'days_present'];
const GOOD_LOW: SortKey[] = ['avg_completion_minutes', 'idle_while_waiting_minutes', 'idle_pct', 'days_idle_30', 'pending_over_24h'];

const ServiceTeamPerformance: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [params, setParams] = useSearchParams();
  const ok = (d: string | null) => /^\d{4}-\d{2}-\d{2}$/.test(d || '');
  const [defFrom, defTo] = PRESETS[2].range();
  const from = ok(params.get('from')) ? params.get('from')! : defFrom;
  const to = ok(params.get('to')) ? params.get('to')! : defTo;
  const picked = params.get('user') || '';
  const [data, setData] = useState<TeamPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'completed', desc: true });

  const set = (next: Record<string, string | null>) => {
    const p: Record<string, string> = { from, to, ...(picked ? { user: picked } : {}) };
    for (const [k, v] of Object.entries(next)) { if (v) p[k] = v; else delete p[k]; }
    setParams(p, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceCallsApi.getTeamPerformance(from, to);
      setData(res.data);
    } catch (e: any) {
      showError('Could not load team performance', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const staff = useMemo(() => {
    const list = [...(data?.staff || [])];
    const { key, desc } = sort;
    list.sort((a, b) => {
      const x = a[key] as any, y = b[key] as any;
      if (key === 'name') return desc ? String(y).localeCompare(String(x)) : String(x).localeCompare(String(y));
      const nx = x === null || x === undefined ? -Infinity : x, ny = y === null || y === undefined ? -Infinity : y;
      return desc ? ny - nx : nx - ny;
    });
    return list;
  }, [data, sort]);

  // Best and worst value per column, among people who worked in the range.
  const marks = useMemo(() => {
    const out: Partial<Record<SortKey, { best: number; worst: number }>> = {};
    const present = (data?.staff || []).filter(s => s.days_present > 0);
    if (present.length < 2) return out;
    for (const k of [...GOOD_HIGH, ...GOOD_LOW]) {
      const vals = present.map(s => s[k] as number | null).filter((v): v is number => v !== null && v !== undefined);
      if (vals.length < 2 || Math.max(...vals) === Math.min(...vals)) continue;
      const hi = Math.max(...vals), lo = Math.min(...vals);
      out[k] = GOOD_HIGH.includes(k) ? { best: hi, worst: lo } : { best: lo, worst: hi };
    }
    return out;
  }, [data]);

  const tone = (k: SortKey, v: any) => {
    const m = marks[k]; if (!m || v === null || v === undefined) return '';
    if (v === m.best) return 'text-emerald-700 font-bold';
    if (v === m.worst) return 'text-red-600 font-bold';
    return '';
  };

  const cell = (s: TeamPerfStaff, k: SortKey): React.ReactNode => {
    const v = s[k] as any;
    switch (k) {
      case 'name': return s.name;
      case 'avg_completion_minutes': case 'max_completion_minutes': return dur(v);
      case 'idle_while_waiting_minutes': return hm(v);
      case 'free_minutes': return hours(v);
      case 'busy_pct': case 'idle_pct': return pctText(v);
      case 'completed_per_day': return v ?? '—';
      default: return v || '—';
    }
  };

  const person = data?.staff.find(s => s.user_id === picked) || null;
  const team = data?.team;
  const presentStaff = (data?.staff || []).filter(s => s.days_present > 0);
  const teamPerDay = presentStaff.length
    ? Math.round((presentStaff.reduce((n, s) => n + s.completed, 0) / presentStaff.reduce((n, s) => n + s.days_present, 0)) * 10) / 10
    : null;

  const openDay = (userId: string, date: string) => navigate(`/service/team/day?user=${encodeURIComponent(userId)}&date=${date}`);
  const activePreset = PRESETS.find(p => { const [a, b] = p.range(); return a === from && b === to; })?.key;

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="bg-white rounded-lg shadow p-4 max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={() => navigate('/service/team')} className="p-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-50" title="Back to the team board">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <TrendingUp className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-800 flex-1">Team Performance</h1>
          <div className="flex flex-wrap gap-1">
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => { const [a, b] = p.range(); set({ from: a, to: b }); }}
                className={`px-2.5 py-1 text-xs rounded border ${activePreset === p.key ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >{p.label}</button>
            ))}
          </div>
          <input type="date" value={from} max={to} onChange={e => e.target.value && set({ from: e.target.value })} className="px-2 py-1 text-sm border border-gray-300 rounded" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={to} min={from} max={todayIST()} onChange={e => e.target.value && set({ to: e.target.value })} className="px-2 py-1 text-sm border border-gray-300 rounded" />
          <button onClick={load} disabled={loading} className="p-2 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {!data ? (
          <div className="py-12 text-center text-sm text-gray-400">{loading ? 'Adding up the days…' : 'No data.'}</div>
        ) : data.staff.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">No service users. Set Resource Type to Service or Both in the user master.</div>
        ) : (
          <>
            {data.from !== from || data.to !== to ? (
              <div className="text-[11px] text-amber-700 mb-2">Showing {data.from} to {data.to} (at most 92 days, up to today).</div>
            ) : null}

            {/* Team at a glance */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              {[
                { l: 'Completed', v: String(team!.completed), s: `${teamPerDay ?? '—'} per person per day` },
                { l: 'Avg time', v: dur(team!.avg_completion_minutes), s: 'taken to closed' },
                { l: 'Busy', v: pctText(team!.busy_pct), s: `${hours(team!.busy_minutes)} of ${hours(team!.shift_minutes)} on shift` },
                { l: 'Idle while calls waited', v: hm(team!.idle_while_waiting_minutes), s: `${pctText(team!.idle_pct)} of shift time`, bad: (team!.idle_while_waiting_minutes || 0) >= 30 },
                { l: 'Holds', v: String(team!.holds || '—'), s: 'calls put on hold' },
                { l: 'Pending >24h now', v: String(team!.pending_over_24h || '—'), s: 'in hand a day or more', bad: team!.pending_over_24h > 0 },
              ].map(c => (
                <div key={c.l} className={`rounded-lg border p-3 ${c.bad ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="text-[11px] font-bold uppercase text-gray-500">{c.l}</div>
                  <div className={`text-xl font-bold ${c.bad ? 'text-red-700' : 'text-gray-800'}`}>{c.v}</div>
                  <div className="text-[11px] text-gray-500">{c.s}</div>
                </div>
              ))}
            </div>

            {/* Every person, ranked */}
            <div className="text-[11px] font-bold uppercase text-gray-500 mb-1">All staff — click a column to sort, a name to see that person</div>
            <div className="overflow-x-auto border border-gray-200 rounded mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
                  <tr>
                    <th className="px-2 py-2 text-right">#</th>
                    {COLS.map(c => (
                      <th
                        key={c.key}
                        title={c.title}
                        onClick={() => setSort(s => ({ key: c.key, desc: s.key === c.key ? !s.desc : c.key !== 'name' }))}
                        className={`px-2 py-2 cursor-pointer select-none whitespace-nowrap hover:text-gray-800 ${c.right ? 'text-right' : 'text-left'} ${sort.key === c.key ? 'text-blue-700' : ''}`}
                      >
                        <span className="inline-flex items-center gap-0.5">{c.label}{sort.key === c.key && <ArrowUpDown className="h-3 w-3" />}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {staff.map((s, i) => (
                    <tr
                      key={s.user_id}
                      onClick={() => set({ user: picked === s.user_id ? null : s.user_id })}
                      className={`cursor-pointer hover:bg-blue-50/50 ${picked === s.user_id ? 'bg-blue-50' : ''} ${s.days_present === 0 ? 'text-gray-400' : ''}`}
                    >
                      <td className="px-2 py-2 text-right text-gray-400 tabular-nums">{i + 1}</td>
                      {COLS.map(c => (
                        <td key={c.key} className={`px-2 py-2 tabular-nums whitespace-nowrap ${c.right ? 'text-right' : 'font-semibold text-gray-800'} ${tone(c.key, s[c.key])}`}>
                          {cell(s, c.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* One person against the team */}
            {person && (
              <div className="border border-blue-200 rounded mb-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-200">
                  <span className="font-semibold text-blue-900 flex-1">{person.name} — {data.from} to {data.to}</span>
                  <button onClick={() => set({ user: null })} className="p-1 rounded hover:bg-blue-100 text-blue-700" title="Close"><X className="h-4 w-4" /></button>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
                    {(() => {
                      const vs = (mine: number | null, avg: number | null, lowerBetter: boolean, fmt: (n: number) => string) => {
                        if (mine === null || avg === null) return { txt: 'team —', cls: 'text-gray-400' };
                        const better = lowerBetter ? mine < avg : mine > avg;
                        const same = Math.abs(mine - avg) < 0.05;
                        return { txt: `team ${fmt(avg)}`, cls: same ? 'text-gray-500' : better ? 'text-emerald-700' : 'text-red-600' };
                      };
                      const items = [
                        { l: 'Days present', v: String(person.days_present), c: { txt: `of ${data.days.length} days`, cls: 'text-gray-500' } },
                        { l: 'Completed / day', v: String(person.completed_per_day ?? '—'), c: vs(person.completed_per_day, teamPerDay, false, n => String(n)) },
                        { l: 'Avg time', v: dur(person.avg_completion_minutes), c: vs(person.avg_completion_minutes, team!.avg_completion_minutes, true, n => dur(n)) },
                        { l: 'Busy', v: pctText(person.busy_pct), c: vs(person.busy_pct, team!.busy_pct, false, n => `${n}%`) },
                        { l: 'Idle while calls waited', v: hm(person.idle_while_waiting_minutes), c: vs(person.idle_pct, team!.idle_pct, true, n => `${n}%`) },
                        { l: 'Days idle 30m+', v: String(person.days_idle_30 || '—'), c: { txt: `${person.holds || 0} holds · ${person.pending_over_24h} pending >24h`, cls: 'text-gray-500' } },
                      ];
                      return items.map(it => (
                        <div key={it.l} className="rounded-lg border border-gray-200 p-3">
                          <div className="text-[11px] font-bold uppercase text-gray-500">{it.l}</div>
                          <div className="text-xl font-bold text-gray-800">{it.v}</div>
                          <div className={`text-[11px] ${it.c.cls}`}>{it.c.txt}</div>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="overflow-auto max-h-[420px] border border-gray-200 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 sticky top-0">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Date</th>
                          <th className="px-2 py-1.5 text-right">On shift</th>
                          <th className="px-2 py-1.5 text-right">Taken</th>
                          <th className="px-2 py-1.5 text-right">Completed</th>
                          <th className="px-2 py-1.5 text-right">Avg time</th>
                          <th className="px-2 py-1.5 text-right">Busy</th>
                          <th className="px-2 py-1.5 text-right">Busy %</th>
                          <th className="px-2 py-1.5 text-right">Idle while calls waited</th>
                          <th className="px-2 py-1.5 text-right">Holds</th>
                          <th className="px-2 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...data.daily].reverse().map(d => {
                          const u = d.users[person.user_id];
                          if (!u) return null;
                          const off = !u.shift_minutes;
                          const { dm, wd } = dayLabel(d.date);
                          return (
                            <tr key={d.date} className={off ? 'text-gray-400' : u.idle_while_waiting_minutes >= 30 ? 'bg-red-50/60' : ''}>
                              <td className="px-2 py-1.5 whitespace-nowrap"><span className="font-semibold">{dm}</span> <span className="text-gray-400">{wd}</span></td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{off ? 'not checked in' : hours(u.shift_minutes)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{u.taken || '—'}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{u.completed || '—'}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{dur(u.avg_completion_minutes)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{dur(u.busy_minutes)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{u.shift_minutes ? `${Math.round((u.busy_minutes / u.shift_minutes) * 100)}%` : '—'}</td>
                              <td className={`px-2 py-1.5 text-right tabular-nums ${u.idle_while_waiting_minutes >= 30 ? 'text-red-600 font-bold' : ''}`}>{dur(u.idle_while_waiting_minutes)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{u.holds || '—'}</td>
                              <td className="px-2 py-1.5 text-right">
                                {!off && <button onClick={() => openDay(person.user_id, d.date)} className="text-blue-600 hover:underline whitespace-nowrap">Timeline →</button>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Day by day, everyone */}
            <div className="text-[11px] font-bold uppercase text-gray-500 mb-1">
              Day by day — calls completed; red is 30m+ idle while calls waited. Click a day to open the timeline.
            </div>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="text-xs">
                <thead className="bg-gray-50 text-[10px] text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left sticky left-0 bg-gray-50 z-10 min-w-[140px]">USER</th>
                    {data.days.map(d => {
                      const { dm, wd } = dayLabel(d);
                      return <th key={d} className="px-1.5 py-1.5 text-center whitespace-nowrap font-medium"><div>{dm}</div><div className="text-gray-400">{wd}</div></th>;
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {staff.map(s => (
                    <tr key={s.user_id} className={picked === s.user_id ? 'bg-blue-50' : ''}>
                      <td className={`px-2 py-1 font-semibold text-gray-800 sticky left-0 z-10 whitespace-nowrap ${picked === s.user_id ? 'bg-blue-50' : 'bg-white'}`}>{s.name}</td>
                      {data.daily.map(d => {
                        const u = d.users[s.user_id];
                        if (!u || !u.shift_minutes) return <td key={d.date} className="px-1.5 py-1 text-center text-gray-200">·</td>;
                        const bad = u.idle_while_waiting_minutes >= 30;
                        return (
                          <td
                            key={d.date}
                            onClick={() => openDay(s.user_id, d.date)}
                            title={`${s.name} · ${d.date}\nCompleted ${u.completed} · busy ${dur(u.busy_minutes)} · idle while calls waited ${dur(u.idle_while_waiting_minutes)}`}
                            className={`px-1.5 py-1 text-center cursor-pointer tabular-nums hover:ring-1 hover:ring-blue-400 ${bad ? 'bg-red-100 text-red-800 font-bold' : u.completed ? 'text-gray-800' : 'text-gray-400'}`}
                          >
                            {u.completed || '0'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-bold text-gray-700">
                  <tr>
                    <td className="px-2 py-1 sticky left-0 bg-gray-50 z-10">Team</td>
                    {data.daily.map(d => (
                      <td key={d.date} className="px-1.5 py-1 text-center tabular-nums" title={`${d.team.present} present · idle while calls waited ${dur(d.team.idle_while_waiting_minutes)}`}>
                        {d.team.present ? d.team.completed : '·'}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
              Each figure is the daily Service Team board added up over the range, so a single day matches the board and the
              day timeline. Green and red mark the best and worst in a column among people present. Busy and idle are shares of
              check-in to check-out time; a day without check-out counts nine hours. Pending and on-hold are as of now.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceTeamPerformance;
